import { isBoolean } from "@sinch/feature-flags";
import { useFeatureFlag } from "@sinch/feature-flags/react";
import { useMemo } from "react";
import { useUser } from "../../api/hooks/useUser";
import { Router } from "../../components/Router/Router";
import { getRouterConfig } from "../../components/Router/utils";
import { config } from "../../config";
import { useInboxConfig } from "../../hooks/useInboxConfig";
import { getNewUniqueRouteKey } from "../../navigationConfig";
import { traceManager } from "../../sentry";
import { useAnalytics, usePublicRouteAnalytics } from "./hooks/useAnalytics";
import {
  mmNavigationConfig,
  nonMenuRoutes,
  mfeLoginRoutes,
  mfeLoginPaths,
  MessageMediaPublicFacingRoutes,
} from "./navigationConfig";

export const PublicRoutes = () => {
  const enablePublicFacingOnShell = useFeatureFlag(
    "enablePublicFacingOnShell",
    isBoolean,
    false
  );

  usePublicRouteAnalytics();

  const routerConfig = useMemo(() => {
    let publicRoutes = nonMenuRoutes;

    publicRoutes = publicRoutes
      .filter((item) => !mfeLoginPaths.includes(item.path))
      .concat(mfeLoginRoutes);

    if (enablePublicFacingOnShell) {
      publicRoutes = publicRoutes
        .filter((item) => !config.AUTH_CONFIG.publicPaths.includes(item.path))
        .concat(MessageMediaPublicFacingRoutes);
    }

    return getRouterConfig([
      ...publicRoutes,
      /**
       * We want to fallback to the hub, as there are still paths leading
       * to it we don't know about, in the future when we want to add our own
       * 404 page we will need to make sure every paths loading the hub are
       * in the config
       */
      {
        key: getNewUniqueRouteKey("fallback"),
        path: "/*",
        mfe: { name: "container", entry: "app" },
      },
    ]);
  }, [enablePublicFacingOnShell]);
  // MM MFE's scrollable element is the html element by default since this Router is not wrapped in a overflow: hidden parent
  return <Router config={routerConfig} forceMfeScroll={false} />;
};

export const AuthenticatedRoutes = ({ startTime }: { startTime: number }) => {
  const trace = traceManager.start("authenticate-user", {
    parentSpan: traceManager.getTransaction("TimeToFirstMFELoad") ?? null,
    startTime,
  });

  const [user] = useUser();
  const inboxConfig = useInboxConfig();
  useAnalytics();

  const routerConfig = useMemo(
    () =>
      getRouterConfig(
        [
          ...mmNavigationConfig,
          ...nonMenuRoutes,
          /**
           * We want to fallback to the hub, as there are still paths leading
           * to it we don't know about, in the future when we want to add our own
           * 404 page we will need to make sure every paths loading the hub are
           * in the config
           */
          {
            key: getNewUniqueRouteKey("fallback"),
            path: "/*",
            mfe: { name: "container", entry: "app" },
          },
        ],
        {
          userRights: user?.rights ?? [],
          inboxVersion: inboxConfig.inboxVersion,
        }
      ),
    [user, inboxConfig]
  );

  if (user) {
    trace.end();
  }

  // MM MFE's scrollable element is the html element by default since this Router is not wrapped in a overflow: hidden parent
  return <Router config={routerConfig} forceMfeScroll={false} />;
};
